<template>
    <v-dialog
        v-model="dialog"
        :persistent="isPersistent"
        width="600px"
    >
        <div class="reassign-tree-item__wrapper">
            <div class="reassign-tree-item__header">
                <icon v-if="'Organization' === item.type" icon="suitcase" class="reassign-tree-item__icon" :width="20" :height="20" />
                <icon v-if="'Fleet' === item.type" icon="truck-solid" class="reassign-tree-item__icon" :width="20" :height="20" />
                <icon v-if="'Vehicle' === item.type" icon="event-car" class="reassign-tree-item__icon" :width="20" :height="20" />
                <icon v-if="'Device' === item.type" icon="device" class="reassign-tree-item__icon" :width="20" :height="20" />
                <div class="reassign-tree-item__header__title"> {{ headerText }} </div>
                <icon icon="close" class="reassign-tree-item__icon--close" color="#fff" :width="20" :height="20" @click.native="onClose" />
            </div>
            <div class="reassign-tree-item__content">
                <div class="reassign-tree-item__content__sub-title">
                    Choose new <span class="bold-text"> {{ subHeaderText }} </span> for <span class="bold-text lowercase"> {{ item.type }} </span> - "{{ item.name }}"
                </div>
                <div class="reassign-tree-item__content__autocomplete-wrapper">
                    <div v-for="entity in entities" :key="`${entity}`" class="reassign-tree-item__content__autocomplete-title">

                        <div class="reassign-tree-item__content__autocomplete-title bold-text"> Select destination {{ entity }} </div>
                        <organization-autocomplete
                            v-if="'Organization' === entity"
                            :disabled="!!(fleet || vehicle)"
                            v-model="organization"
                            class="small-field active-modal__select"
                        />
                        <fleet-autocomplete
                            v-if="'Fleet' === entity"
                            :disabled="!!(organization || vehicle)"
                            v-model="fleet"
                            class="small-field active-modal__select"
                        />
                        <vehicle-autocomplete
                            v-if="'Vehicle' === entity"
                            :disabled="!!(organization || fleet)"
                            v-model="vehicle"
                            small-field
                            class="active-modal__select"
                        />
                    </div>
                </div>
            </div>
            <div class="reassign-tree-item__actions-wrapper">
                <sp-btn class="reassign-tree-item__cancel" icon="go-back" text="CANCEL" @click.native="onClose"></sp-btn>
                <sp-btn class="reassign-tree-item__confirm" :disabled="!(organization || vehicle || fleet)" :loading="isLoading" icon="download" text="CONFIRM" @click.native="onReassign"></sp-btn>
            </div>
        </div>
    </v-dialog>
</template>

<script>
import Icon from '@/components/atoms/Icon'
import SpBtn from '@/components/forms/SpBtn'
import FleetAutocomplete from '@/views/Fleets/components/FleetAutocomplete'
import OrganizationAutocomplete from '@/views/Organizations/components/OrganizationAutocomplete'
import VehicleAutocomplete from '@/views/Vehicles/components/VehicleAutocomplete'
import DeviceAutocomplete from '../../Devices/components/DeviceAutocomplete'
import OrganizationTreeService from '@/services/OrganizationTreeService'
import TreeApi from '../../../api/TreeApi'
import OrganizationTreeTransformer from '@/transformers/OrganizationTreeTransformer'

const treeApi = new TreeApi()
const organizationTreeTransformer = new OrganizationTreeTransformer()

export default {
    name: 'ReassignTreeItemModal',
    components: {
        DeviceAutocomplete,
        Icon,
        SpBtn,
        FleetAutocomplete,
        OrganizationAutocomplete,
        VehicleAutocomplete
    },
    data () {
        return {
            dialog: false,
            fleet: null,
            organization: null,
            vehicle: null,
            device: null,
            isPersistent: false,
            entities: [],
            isLoading: false
        }
    },
    props: {
        value: {
            type: Boolean,
            default: false
        },
        item: {
            type: [Object, null],
            default: null,
            required: true
        },
        handleReassignNode: {
            type: Function,
            required: true
        }
    },
    computed: {
        result: {
            get () {
                return this.value
            },
            set (value) {
                this.$emit('input', this.dialog)
            }
        },
        headerText () {
            switch (this.item.type) {
                case 'Organization':
                case 'Fleet':
                    return `Choose Organization for ${this.item.type}`
                    break
                case 'Vehicle':
                    return `Choose Fleet for ${this.item.type}`
                    break
                default:
                    return `Choose Organization or Fleet or Vehicle for ${this.item.type}`
            }
        },
        subHeaderText () {
            switch (this.item.type) {
                case 'Organization':
                case 'Fleet':
                    return `Organization`
                    break
                case 'Vehicle':
                    return `Fleet`
                    break
                default:
                    return `Organization or Fleet or Vehicle`
            }
        }
    },
    watch: {
        value: {
            handler () {
                if (this.value) {
                    switch (this.item.type) {
                        case 'Organization':
                            this.entities = ['Organization']
                            break
                        case 'Fleet':
                            this.entities = ['Organization']
                            break
                        case 'Vehicle':
                            this.entities = ['Fleet']
                            break
                        default:
                            this.entities = ['Organization', 'Fleet', 'Vehicle']
                    }
                } else {
                    this.resetComponentData()
                }

                this.dialog = this.value
            }
        },
        dialog (val) {
            this.$emit('input', val)
        }
    },
    methods: {
        async onReassign () {
            this.isPersistent = true
            this.isLoading = true

            const pickedDestinationNode = this.organization || this.fleet || this.vehicle

            pickedDestinationNode.type = ''

            if (this.organization) {
                pickedDestinationNode.type = 'Organization'
            }

            if (this.fleet) {
                pickedDestinationNode.type = 'Fleet'
            }

            if (this.vehicle) {
                pickedDestinationNode.type = 'Vehicle'
            }

            this.handleReassignNode(pickedDestinationNode.id, this.item)

            await OrganizationTreeService.moveItem(this.item.type, this.item.id, pickedDestinationNode.type, pickedDestinationNode.id)

            // reload tree
            const response = await treeApi.getTree()
            this.treeData = response
            this.totalResults = response.payload.totalResults

            const treeObject = organizationTreeTransformer.transform(response.payload)

            this.$store.commit('organizationTreeModule/setLatestFullTree', treeObject)

            this.isLoading = false
            this.isPersistent = false
            this.dialog = false
            this.resetComponentData()
        },
        resetComponentData () {
            this.fleet = null
            this.organization = null
            this.vehicle = null
            this.device = null
            this.entities = []
            this.isLoading = false
        },
        onClose () {
            if (!this.isPersistent) {
                this.dialog = !this.dialog
            }
        }
    }
}
</script>

<style scoped lang="scss">
.reassign-tree-item {
    &__wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;
        background: $white;
    }
    &__header {
        display: flex;
        flex-direction: row;
        background-color: $secondary;
        align-items: center;
        padding: 15px 20px;
        &__title {
            font-size: 18px;
            color: $white;
            font-weight: 700;
            margin: 0 auto 0 10px;
        }
    }

    &__content {
        padding: 10px 20px;

        &__sub-title {
            margin-bottom: 14px;
        }
        &__autocomplete-title {
            font-size: 14px;
            margin-bottom: 7px;
        }
    }

    &__icon {
        color: $font;
        &--close {
            cursor: pointer;
        }
    }

    &__actions-wrapper {
        padding: 10px 20px 20px;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
}

.bold-text {
    font-weight: 700;
}
.lowercase {
    text-transform: lowercase;
}

.active-modal__select {
    width: 100%;
    margin: 0 auto;

    ::v-deep .v-input__slot {
        border: 2px solid #eeeeee !important;
        border-radius: 10px !important;
        min-height: 40px !important;
    }
}
</style>
